import React, { useState, useEffect, useCallback } from 'react';
import { getApiUrl } from '@/utils/authUtils';
import { useNavigate } from 'react-router-dom';
import { Header } from '@/components/Header';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { useLanguage } from '@/context/LanguageContext';
import { useAuth } from '@/context/AuthContext';
import { PixelAvatar } from '@/components/PixelAvatar';
import ChallengeCompletionPopup from '@/components/ChallengeCompletionPopup';
import { 
  Trophy, Calendar, Target, Users, Flame, Star, 
  ChevronRight, Clock, Award, Zap, ArrowLeft
} from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '@/hooks/useFetch';

const API_URL = getApiUrl();

const ChallengesPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const { isSpanish } = useLanguage();
  
  const [challenges, setChallenges] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // Challenge completion popup state
  const [showCompletionPopup, setShowCompletionPopup] = useState(false);
  const [completedChallenge, setCompletedChallenge] = useState(null);
  const [lastCompletedStates, setLastCompletedStates] = useState({
    daily: false,
    weekly: false,
    monthly: false
  });

  const fetchChallenges = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data } = await apiFetch('/api/challenges/overview');
      if (data) {
        setChallenges(data);
        
        // Check if any challenge was just completed
        if (data.daily?.progress?.completed && !lastCompletedStates.daily) {
          setCompletedChallenge({
            type: 'daily',
            title: isSpanish ? data.daily.titleEs : data.daily.title,
            xpEarned: data.daily.reward_xp
          });
          setShowCompletionPopup(true);
          setLastCompletedStates(prev => ({ ...prev, daily: true }));
        } else if (data.weekly?.progress?.completed && !lastCompletedStates.weekly) {
          setCompletedChallenge({
            type: 'weekly',
            title: isSpanish ? data.weekly.titleEs : data.weekly.title,
            xpEarned: data.weekly.reward_xp
          });
          setShowCompletionPopup(true);
          setLastCompletedStates(prev => ({ ...prev, weekly: true }));
        } else if (data.monthly?.progress?.completed && !lastCompletedStates.monthly) {
          setCompletedChallenge({
            type: 'monthly',
            title: isSpanish ? data.monthly.titleEs : data.monthly.title,
            xpEarned: data.monthly.reward_xp
          });
          setShowCompletionPopup(true);
          setLastCompletedStates(prev => ({ ...prev, monthly: true }));
        }
      }
    } catch (error) {
      console.error('Failed to fetch challenges:', error);
    } finally {
      setIsLoading(false);
    }
  }, [isSpanish, lastCompletedStates]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchChallenges();
    }
  }, [isAuthenticated, fetchChallenges]);

  // Redirect if not authenticated
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [authLoading, isAuthenticated, navigate]);

  if (authLoading || isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-[#00E5CC] border-t-transparent rounded-full" />
      </div>
    );
  }

  const daily = challenges?.daily;
  const weekly = challenges?.weekly;
  const monthly = challenges?.monthly;
  const friendChallenges = challenges?.friend_challenges || [];

  return (
    <div className="min-h-screen bg-background pb-20">
      <Header />
      
      <main className="container max-w-3xl mx-auto px-4 py-6 space-y-6">
        {/* Back Button */}
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => navigate(-1)}
          className="mb-2"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          {isSpanish ? 'Volver' : 'Back'}
        </Button>

        {/* Page Header */}
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold flex items-center justify-center gap-2">
            <Trophy className="h-6 w-6 text-yellow-500" />
            {isSpanish ? 'Desafíos' : 'Challenges'}
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            {isSpanish 
              ? 'Completa desafíos para ganar recompensas' 
              : 'Complete challenges to earn rewards'}
          </p>
        </div>

        {/* Daily Challenge */}
        {daily && (
          <Card className="border-orange-500/30 bg-gradient-to-br from-orange-500/10 to-transparent">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Flame className="h-5 w-5 text-orange-500" />
                  {isSpanish ? 'Desafío Diario' : 'Daily Challenge'}
                </CardTitle>
                <Badge variant="outline" className="text-orange-500 border-orange-500">
                  +{daily.reward_xp} XP
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <h3 className="font-semibold mb-1">
                {isSpanish ? daily.titleEs : daily.title}
              </h3>
              <p className="text-sm text-muted-foreground mb-3">
                {isSpanish ? daily.descriptionEs : daily.description}
              </p>
              
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>{isSpanish ? 'Progreso' : 'Progress'}</span>
                  <span className={daily.progress.completed ? 'text-green-500' : ''}>
                    {daily.progress.current} / {daily.target}
                    {daily.progress.completed && ' ✓'}
                  </span>
                </div>
                <Progress 
                  value={(daily.progress.current / daily.target) * 100} 
                  className="h-2"
                />
              </div>
              
              {daily.progress.completed && (
                <div className="mt-3 p-2 bg-green-500/20 rounded-lg text-center text-sm text-green-400">
                  🎉 {isSpanish ? '¡Completado!' : 'Completed!'}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Weekly Challenge */}
        {weekly && (
          <Card className="border-blue-500/30 bg-gradient-to-br from-blue-500/10 to-transparent">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-blue-500" />
                  {isSpanish ? 'Desafío Semanal' : 'Weekly Challenge'}
                </CardTitle>
                <Badge variant="outline" className="text-blue-500 border-blue-500">
                  +{weekly.reward_xp} XP
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <h3 className="font-semibold mb-1">
                {isSpanish ? weekly.titleEs : weekly.title}
              </h3>
              <p className="text-sm text-muted-foreground mb-3">
                {isSpanish ? weekly.descriptionEs : weekly.description}
              </p>
              
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>{isSpanish ? 'Días Activos' : 'Active Days'}</span>
                  <span className={weekly.progress.completed ? 'text-green-500' : ''}>
                    {weekly.progress.active_days} / {weekly.goal_days}
                    {weekly.progress.completed && ' ✓'}
                  </span>
                </div>
                <Progress 
                  value={(weekly.progress.active_days / weekly.goal_days) * 100} 
                  className="h-2"
                />
              </div>

              {/* Mini Leaderboard */}
              {weekly.leaderboard?.length > 0 && (
                <div className="mt-4 pt-3 border-t border-border/50">
                  <p className="text-xs text-muted-foreground mb-2">
                    {isSpanish ? 'Líderes de la semana' : 'This Week\'s Leaders'}
                  </p>
                  <div className="flex gap-2 overflow-x-auto">
                    {weekly.leaderboard.slice(0, 3).map((leader, idx) => (
                      <div key={leader.user_id || leader.display_name || `leader-${idx}`} className="flex items-center gap-1 bg-muted/50 rounded-lg px-2 py-1">
                        <span className="text-xs">{idx === 0 ? '🥇' : idx === 1 ? '🥈' : '🥉'}</span>
                        <PixelAvatar avatarId={leader.avatar} size={20} showBorder={false} />
                        <span className="text-xs truncate max-w-[60px]">{leader.display_name}</span>
                        <span className="text-xs text-muted-foreground">{leader.active_days}d</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Monthly Challenge */}
        {monthly && (
          <Card className="border-purple-500/30 bg-gradient-to-br from-purple-500/10 to-transparent">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Star className="h-5 w-5 text-purple-500" />
                  {isSpanish ? 'Desafío Mensual' : 'Monthly Challenge'}
                </CardTitle>
                <Badge variant="outline" className="text-purple-500 border-purple-500">
                  +{monthly.reward_xp} XP
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <h3 className="font-semibold mb-1">
                {isSpanish ? monthly.titleEs : monthly.title}
              </h3>
              <p className="text-sm text-muted-foreground mb-1">
                {isSpanish ? monthly.descriptionEs : monthly.description}
              </p>
              <p className="text-xs text-purple-400 mb-3">
                {isSpanish ? 'Categoría: ' : 'Category: '}{monthly.category}
              </p>
              
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>{isSpanish ? 'Frases vistas' : 'Phrases Viewed'}</span>
                  <span className={monthly.progress.completed ? 'text-green-500' : ''}>
                    {monthly.progress.current} / {monthly.target}
                    {monthly.progress.completed && ' ✓'}
                  </span>
                </div>
                <Progress 
                  value={(monthly.progress.current / monthly.target) * 100} 
                  className="h-2"
                />
              </div>

              {/* View Category Button */}
              <Button 
                variant="outline" 
                size="sm" 
                className="w-full mt-3"
                onClick={() => navigate(`/library?category=${monthly.category}`)}
              >
                <Target className="h-4 w-4 mr-2" />
                {isSpanish ? 'Ver Categoría' : 'View Category'}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Friend Challenges */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="h-5 w-5 text-[#00E5CC]" />
              {isSpanish ? 'Desafíos con Amigos' : 'Friend Challenges'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {friendChallenges.length === 0 ? (
              <div className="text-center py-4">
                <p className="text-muted-foreground text-sm mb-3">
                  {isSpanish 
                    ? 'No tienes desafíos activos con amigos' 
                    : 'No active friend challenges'}
                </p>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => navigate('/friends')}
                >
                  <Users className="h-4 w-4 mr-2" />
                  {isSpanish ? 'Desafiar a un Amigo' : 'Challenge a Friend'}
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {friendChallenges.map((challenge) => {
                  const isChallenger = challenge.challenger_id === user?.user_id;
                  const opponent = isChallenger ? challenge.challenged_name : challenge.challenger_name;
                  const opponentAvatar = isChallenger ? challenge.challenged_avatar : challenge.challenger_avatar;
                  const myStreak = isChallenger ? challenge.challenger_streak : challenge.challenged_streak;
                  const theirStreak = isChallenger ? challenge.challenged_streak : challenge.challenger_streak;
                  
                  return (
                    <div 
                      key={challenge.challenge_id}
                      className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <PixelAvatar avatarId={opponentAvatar} size={40} />
                        <div>
                          <p className="font-medium text-sm">{opponent}</p>
                          <p className="text-xs text-muted-foreground">
                            {challenge.duration_days} {isSpanish ? 'días' : 'days'}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold">
                          <span className="text-[#00E5CC]">{myStreak}</span>
                          <span className="text-muted-foreground mx-1">vs</span>
                          <span className="text-orange-500">{theirStreak}</span>
                        </p>
                        <Badge variant="outline" className="text-xs">
                          {challenge.status === 'pending' 
                            ? (isSpanish ? 'Pendiente' : 'Pending')
                            : (isSpanish ? 'Activo' : 'Active')}
                        </Badge>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* XP Summary */}
        <Card className="bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border-yellow-500/30">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Zap className="h-5 w-5 text-yellow-500" />
                <span className="font-medium">
                  {isSpanish ? 'XP Potencial Hoy' : 'Potential XP Today'}
                </span>
              </div>
              <span className="text-xl font-bold text-yellow-500">
                +{(daily?.reward_xp || 0) + (weekly?.reward_xp || 0) + (monthly?.reward_xp || 0)} XP
              </span>
            </div>
          </CardContent>
        </Card>
      </main>
      
      {/* Challenge Completion Popup */}
      <ChallengeCompletionPopup
        isOpen={showCompletionPopup}
        onClose={() => setShowCompletionPopup(false)}
        challengeType={completedChallenge?.type}
        challengeTitle={completedChallenge?.title}
        xpEarned={completedChallenge?.xpEarned || 0}
        levelUp={false}
        newLevel={null}
        isSpanish={isSpanish}
      />
    </div>
  );
};

export default ChallengesPage;
